import React from 'react';
import classNames from 'classnames';
import { push } from 'redux-first-history';
import { useDispatch } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';

import { useSelector } from 'hooks';
import { b } from 'utils/i18nUtils';
import { usePatchVehicleCheckDecisionMutation } from 'modules/selfServices/service';
import { useGetVCLinkQuery, useSendVCLinkMutation } from 'modules/vehicleCheck/service';

import formActions from 'modules/form/actions';
import { isPublicDevice } from 'modules/dealers/selectors';
import { getAcceptedVehicleChecks } from 'modules/form/selectors';
import { getCommunicationData } from 'modules/communication/selectors';
import { getSelectedSelfServiceId, getVehicleCheckPublicToken } from 'modules/auth/selectors';

import PageBaseProps from 'types/PageBase';
import { VehicleCheckType } from 'modules/vehicleCheck/types/VehicleCheckModel';
import { FinalInstructionsResponse } from 'modules/dealers/types/FinalInstructionsResponse';

import Button from 'components/ui/Button';
import Spinner from 'components/ui/Spinner';
import Confirm from 'components/ui/Confirm';
import QRCodeViewer from 'components/ui/QRCodeViewer';

import { ChannelEnum } from '../../modules/auth/types/LoginRequest';
import SelectCommunicationChannelModal from '../Communication/SelectCommunicationChannelModal';

type ContentProps = PageBaseProps & Pick<FinalInstructionsResponse, 'vehicleCheckLink' | 'vehicleCheckLinkExpiredAt' | 'vehicleCheckAccepted' | 'courtesyVehicleCheckAccepted'> & {
  onClose?: () => void;
};

const Content: React.FC<ContentProps & PageBaseProps> = ({
  onClose,
  onNext,
  vehicleCheckLink,
  vehicleCheckLinkExpiredAt,
  vehicleCheckAccepted,
  courtesyVehicleCheckAccepted,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [open, setOpen] = React.useState(false);
  const [displayModal, setDisplayModal] = React.useState(false);

  const isPublic = useSelector(isPublicDevice);
  const communicationData = useSelector(getCommunicationData);
  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const vehicleCheckToken = useSelector(getVehicleCheckPublicToken);
  const acceptedVehicleChecks = useSelector(getAcceptedVehicleChecks);

  const nbVehicleCheck = acceptedVehicleChecks.length;
  const displayStartButtonInModal = typeof onClose === 'function' && !isPublic;

  const { isLoading } = useGetVCLinkQuery({ selfServiceId }, { skip: !onNext });
  const [sendLink, { isLoading: isSending }] = useSendVCLinkMutation();
  const [patchVC, { isLoading: isUpdating }] = usePatchVehicleCheckDecisionMutation();

  const expirationTime = React.useMemo(() =>
    intl.formatTime(vehicleCheckLinkExpiredAt, { hour: 'numeric', minute: 'numeric' }),
  [vehicleCheckLinkExpiredAt, intl]);

  const handleRedirectToVehicleCheck = () => {
    window.location.href = vehicleCheckLink;
  };

  const handleOnClickStart = () => {
    dispatch(formActions.resetVCForm());

    if (!isPublic && vehicleCheckToken) {
      if (nbVehicleCheck === 1) {
        dispatch(push(`/vehicle-check/${acceptedVehicleChecks[0]}?token=${vehicleCheckToken}`));
      } else {
        dispatch(push(`/vehicle-check?token=${vehicleCheckToken}`));
      }
    }
  };

  const handleOpenSendLinkModal = React.useCallback(() => setDisplayModal(true), []);

  const handleOnClickSend = async (channel: ChannelEnum) => {
    await sendLink({ selfServiceId, channel }).unwrap();
  };

  const handeOpenCancelVehicleCheck = () => {
    setOpen(true);
  };

  const handleCloseCancelVehicleCheck = () => {
    setOpen(false);
  };

  const handleConfirm = async () => {
    if (vehicleCheckAccepted) {
      await patchVC({ selfServiceId, accepted: false }).unwrap();
    }
    if (courtesyVehicleCheckAccepted) {
      await patchVC({ selfServiceId, accepted: false, type: VehicleCheckType.COURTESY }).unwrap();
    }
    onNext?.();
    onClose?.();
    setOpen(false);
  };

  const vehicleCheckLabel = React.useMemo(() => {
    let labelDescriptor;
    if (courtesyVehicleCheckAccepted) {
      labelDescriptor = { id: 'page.vehicleCheck.qrCodeStep.type.courtesy', defaultMessage: 'courtesy' };
    }
    if (vehicleCheckAccepted && !courtesyVehicleCheckAccepted) {
      labelDescriptor = { id: 'page.vehicleCheck.qrCodeStep.type.personal', defaultMessage: 'personal' };
    }
    if (courtesyVehicleCheckAccepted && vehicleCheckAccepted) {
      labelDescriptor = { id: 'page.vehicleCheck.qrCodeStep.type.personalCourtesy', defaultMessage: 'personal and courtesy' };
    }
    return labelDescriptor ? intl.formatMessage(labelDescriptor) : undefined;
  }, [intl, courtesyVehicleCheckAccepted, vehicleCheckAccepted]);

  return <>
    <main className="self-start !mb-0">
      {isPublic && (
        <div className="text-default max-w-content mx-auto">
          <FormattedMessage
            id="page.vehicleCheck.qrCodeStep.kioskDescription"
            // eslint-disable-next-line max-len
            defaultMessage="Please <b>scan the following QR code</b> to open your guided vehicle check{hasDetails, select, true { ({vehicleCheckDetails})} other{}} or click on the button to <b>receive the link by SMS</b> to the phone number provided in your contact details."
            tagName="p"
            values={{ b, vehicleCheckDetails: vehicleCheckLabel, hasDetails: Boolean(vehicleCheckLabel) }}
          />
        </div>
      )}

      {!isPublic && (
        <div className="text-default">
          <FormattedMessage
            id="page.vehicleCheck.qrCodeStep.startDescription"
            defaultMessage="Start your guided vehicule check{hasDetails, select, true { ({vehicleCheckDetails})} other{}} by clicking the button below."
            tagName="p"
            values={{
              vehicleCheckDetails: vehicleCheckLabel, hasDetails: Boolean(vehicleCheckLabel),
            }}
          />
        </div>
      )}

      <div className="mt-5 text-default">
        <FormattedMessage
          id="page.vehicleCheck.qrCodeStep.linkExpiration"
          defaultMessage="You have until <b>{time}</b> to complete your tour of the vehicle"
          tagName="p"
          values={{
            time: expirationTime,
            b: (text) => b(text, 'text-s kiosk:text-2xl kioskSharebox:text-xl'),
          }}
        />
      </div>

      {displayStartButtonInModal && (
        <Button
          disabled={isLoading}
          testId="start-vehicleCheck"
          onClick={handleRedirectToVehicleCheck}
          className="mt-5"
        >
          <FormattedMessage id="page.vehicleCheck.qrCodeStep.start" defaultMessage="Start the vehicle check" />
        </Button>
      )}

      {isPublic && (
        <div className="flex flex-col items-center">
          {isLoading && <Spinner />}
          {vehicleCheckLink && (
            <div className="aspect-square w-2/3 md:w-1/3 kioskPartteam:w-full kioskSharebox:w-full bg-card-bg rounded-card p-4 kiosk:p-6 mt-12">
              <QRCodeViewer content={vehicleCheckLink} className="!h-auto !w-full rounded-card" options={{ margin: 2 }} />
            </div>
          )}
          <Button className="mt-12 kiosk:px-24" onClick={handleOpenSendLinkModal} loading={isSending} testId="open-send-link-modal">
            <FormattedMessage id="page.vehicleCheck.qrCodeStep.sendLink" defaultMessage="Send me the link" />
          </Button>
          {displayModal && (
            <SelectCommunicationChannelModal
              data={communicationData}
              onSubmit={handleOnClickSend}
              onClose={setDisplayModal}
              maxAttemptReached={false}
              title={{
                id: 'page.vehicleCheck.qrCodeStep.sendLink',
                defaultMessage: 'Send me the link',
              }}
              sendToChannelMessage={{
                id: 'page.vehicleCheck.qrCodeStep.send',
                defaultMessage: 'Send',
              }}
              blackListChannels={[ChannelEnum.EMAIL]}
            />
          )}
        </div>
      )}
    </main>

    <footer className="self-end w-full">
      <div
        className={classNames('font-bold underline cursor-pointer mt-5 justify-center', { 'kiosk:mb-24 mb-36': !onClose })}
        onClick={handeOpenCancelVehicleCheck}
        data-testid="cancel-vehicleCheck"
      >
        <FormattedMessage
          id="page.vehicleCheck.discard"
          defaultMessage="I no longer wish to carry out {nbVehicleCheck, plural, one {a vehicle check} other {vehicle checks}}"
          tagName="p"
          values={{ nbVehicleCheck }}
        />
      </div>
      {onNext && <div className="nextButton">
        {isPublic && (
          <Button className="px-8 kiosk:px-24" onClick={onNext}>
            <FormattedMessage
              id="steps.finish"
              defaultMessage="Finish"
            />
          </Button>
        )}
        {!isPublic && (
          <Button
            disabled={isLoading}
            testId="start-vehicleCheck"
            onClick={handleOnClickStart}
            className="mt-12 kiosk:px-24"
          >
            <FormattedMessage
              id="page.vehicleCheck.qrCodeStep.start"
              defaultMessage="Start the vehicle check"
            />
          </Button>
        )}
      </div>}
      <Confirm
        open={open}
        onCancel={handleCloseCancelVehicleCheck}
        onConfirm={handleConfirm}
        cancel={<FormattedMessage id="header.cancel" defaultMessage="No, continue" />}
        confirm={<FormattedMessage id="header.confirm" defaultMessage="Yes, cancel" />}
        confirmProps={{ loading: isUpdating }}
        title={(
          <h2 className="text-2xl">
            <FormattedMessage id="page.vehicleCheck.qrCodeStep.cancel" defaultMessage="Cancel your vehicle check" />
          </h2>
        )}
        question={(
          <div className="text-center">
            <FormattedMessage
              id="vehicleCheck.selfService.responsability"
              // eslint-disable-next-line max-len
              defaultMessage="By taking a moment to document the vehicle’s condition now, you avoid being held responsible for any damage that has not been previously reported."
              tagName="p"
            />
          </div>
        )}
      />
    </footer>
  </>;
};

export default Content;
